import { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BadgeCheck, Package, Tag, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, CATEGORY_LABELS, CATEGORY_BADGE, stockBadge, formatPrice } from "@/api";

export default function MachineDetail({ machine, open, onClose }) {
  const [photoIdx, setPhotoIdx] = useState(0);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ customer_name: "", customer_email: "", customer_phone: "", preferred_time: "", notes: "" });

  if (!machine) return null;
  const sb = stockBadge(machine.stock);
  const photos = machine.photos?.length ? machine.photos : ["https://images.unsplash.com/photo-1606501126768-b78d4569d3f9?q=85"];

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/orders", { machine_id: machine.id, ...form });
      setSent(true);
      toast.success("Richiesta inviata! Il titolare ti contatterà presto.");
    } catch (err) {
      toast.error(formatApiError(err, "Invio non riuscito. Riprova."));
    } finally {
      setSending(false);
    }
  };

  const close = () => {
    setSent(false);
    setPhotoIdx(0);
    setForm({ customer_name: "", customer_email: "", customer_phone: "", preferred_time: "", notes: "" });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0" data-testid="machine-detail-modal">
        <div className="grid grid-cols-1 md:grid-cols-2">
          <div className="bg-sky-50/60 p-6">
            <div className="rounded-2xl overflow-hidden bg-white border border-slate-200/80 aspect-[4/3]">
              <img src={photos[photoIdx]} alt={machine.name} className="w-full h-full object-cover" data-testid="detail-main-photo" />
            </div>
            {photos.length > 1 && (
              <div className="flex gap-2 mt-3">
                {photos.map((p, i) => (
                  <button key={i} type="button" data-testid={`detail-thumb-${i}`}
                    onClick={() => setPhotoIdx(i)}
                    className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors ${i === photoIdx ? "border-sky-500" : "border-transparent opacity-70 hover:opacity-100"}`}>
                    <img src={p} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
            <div className="mt-5 space-y-2.5 text-sm text-slate-600">
              <p className="flex items-center gap-2"><BadgeCheck className="w-4 h-4 text-sky-600" /> Marca: <strong className="text-slate-900">{machine.brand || "—"}</strong></p>
              <p className="flex items-center gap-2"><Tag className="w-4 h-4 text-sky-600" /> Modello: <strong className="text-slate-900">{machine.model || "—"}</strong></p>
              <p className="flex items-center gap-2"><Package className="w-4 h-4 text-sky-600" /> Disponibilità: <strong className="text-slate-900">{machine.stock} pz</strong></p>
            </div>
          </div>

          <div className="p-6 md:p-8">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className={`rounded-full px-3 py-1 text-xs font-medium ${CATEGORY_BADGE[machine.category] || ""}`} data-testid="detail-category-badge">
                {CATEGORY_LABELS[machine.category] || machine.category}
              </span>
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${sb.cls}`} data-testid="detail-stock-badge">{sb.label}</span>
            </div>
            <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-slate-900 leading-tight" data-testid="detail-name">{machine.name}</h2>
            <p className="text-3xl font-semibold text-sky-700 mt-2" data-testid="detail-price">{formatPrice(machine.price)}</p>
            <p className="text-sm sm:text-base text-slate-600 leading-relaxed mt-4" data-testid="detail-description">{machine.description}</p>

            <div className="border-t border-slate-100 mt-6 pt-6">
              {sent ? (
                <div className="text-center py-8" data-testid="order-success-message">
                  <span className="w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-4">
                    <Send className="w-6 h-6" />
                  </span>
                  <h3 className="font-serif text-xl font-semibold text-slate-900">Richiesta inviata con successo</h3>
                  <p className="text-sm text-slate-500 mt-2 max-w-sm mx-auto">
                    Il titolare dell'officina ti ricontatterà per confermare disponibilità, prezzo e modalità di consegna o ritiro.
                  </p>
                  <Button variant="outline" className="mt-5" onClick={close} data-testid="order-success-close-button">Chiudi</Button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4" data-testid="order-request-form">
                  <h3 className="font-serif text-xl font-semibold text-slate-900">Richiedi questa macchina</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="o-name">Nome e Cognome *</Label>
                      <Input id="o-name" data-testid="order-input-name" required value={form.customer_name}
                        onChange={(e) => setForm({ ...form, customer_name: e.target.value })} />
                    </div>
                    <div>
                      <Label htmlFor="o-phone">Numero di Telefono *</Label>
                      <Input id="o-phone" type="tel" data-testid="order-input-phone" required value={form.customer_phone}
                        onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="o-email">Email di Contatto *</Label>
                    <Input id="o-email" type="email" data-testid="order-input-email" required value={form.customer_email}
                      onChange={(e) => setForm({ ...form, customer_email: e.target.value })} />
                  </div>
                  <div>
                    <Label>Fascia Oraria Preferita per Contatto</Label>
                    <Select value={form.preferred_time} onValueChange={(v) => setForm({ ...form, preferred_time: v })}>
                      <SelectTrigger data-testid="order-select-time"><SelectValue placeholder="Seleziona una fascia oraria" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mattina" data-testid="order-time-mattina">Mattina (9:00 - 12:30)</SelectItem>
                        <SelectItem value="pomeriggio" data-testid="order-time-pomeriggio">Pomeriggio (15:00 - 18:30)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="o-notes">Messaggio o Richieste Particolari</Label>
                    <Textarea id="o-notes" rows={3} data-testid="order-input-notes" value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                  </div>
                  <Button type="submit" disabled={sending} data-testid="order-submit-button"
                    className="w-full bg-sky-600 hover:bg-sky-700 text-white rounded-full py-5">
                    {sending ? "Invio in corso..." : "Invia Richiesta d'Ordine (Senza Impegno)"}
                  </Button>
                  <p className="text-xs text-slate-400 leading-relaxed" data-testid="order-disclaimer">
                    Non è richiesto alcun pagamento online. Verrai contattato dal titolare per confermare disponibilità e dettagli di spedizione o ritiro in officina.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
